import { TFunction } from "i18next";
import { Dispatch, SetStateAction } from "react";
import { enviroment } from "../../../../enviroment";
import { showWarningToast, withToasts } from "../../../components/Toast/Toast";
import { isCreateUserMailNotSentError } from "./userStatus";

/**
 * "Add user" (every caller: the customer edit screen, the dealer's own
 * profile, the admin act-as dialog). Flag userCreateMailStatusEnabled: the
 * API's 502 welcomeMailNotSent (API flag UserCreateHardeningEnabled) means
 * the user WAS created - the repository already refetched the list - so the
 * promise RESOLVES (the dialog closes, the act-as picker auto-selects the new
 * user) with a warning toast that says to use "Resend welcome mail", instead
 * of the generic error toast + an open dialog + an unhandled rejection. Flag
 * off, or any other error = withToasts' error toast and the rejection exactly
 * as today.
 */

export interface CreateUserOutcome {
  /** The user exists, the welcome mail did not go out (the API's 502 marker). */
  mailNotSent: boolean;
}

/** The warning stays long enough to read where the resend button is. */
export const USER_CREATED_MAIL_FAILED_TOAST_MS = 10000;

export async function createUserOutcome(
  create: () => Promise<void>
): Promise<CreateUserOutcome> {
  try {
    await create();
    return { mailNotSent: false };
  } catch (error) {
    if (
      enviroment.userCreateMailStatusEnabled &&
      isCreateUserMailNotSentError(error)
    ) {
      return { mailNotSent: true };
    }
    throw error;
  }
}

export interface CreateUserWithToastsArgs {
  create: () => Promise<void>;
  email: string;
  t: TFunction;
  loadingMessage: string;
  successMessage: string;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export async function createUserWithToasts({
  create,
  email,
  t,
  loadingMessage,
  successMessage,
  setIsLoading,
}: CreateUserWithToastsArgs): Promise<void> {
  const outcome = await withToasts(
    () => createUserOutcome(create),
    loadingMessage,
    successMessage,
    "",
    setIsLoading,
    // No success toast for "created, mail not sent": the warning below says it.
    { showSuccess: (result) => !result.mailNotSent }
  );
  if (outcome.mailNotSent) {
    showWarningToast(
      t("customerUpdate.userCreatedMailFailed", { email }),
      USER_CREATED_MAIL_FAILED_TOAST_MS
    );
  }
}
